import React, { ReactNode, MouseEvent, FunctionComponent, Fragment } from 'react';
import classNames from 'classnames';
import Loader from 'components/loader/loader';
import { ColorGroupAll, ColorGroupBrand, mapColorToClassname } from 'components/enum/global';
import { Action } from 'components/typography';
import {
    ButtonSize,
    ButtonView,
    ButtonStyling,
    ButtonType,
    mapSizeToClassname,
    mapViewToClassname,
    mapStylingToClassname,
    mapTypeToButtonType,
} from './button.config';

export type ButtonProps = {
    id?: string;
    href?: string;
    qaRole?: string;
    text?: ReactNode;
    icon?: ReactNode;
    a11yName?: string;
    size?: ButtonSize;
    view?: ButtonView;
    type?: ButtonType;
    color?: ColorGroupAll;
    styling?: ButtonStyling;
    extraClasses?: string;
    onClick?: (event: MouseEvent) => void;
    hasOpenInBlank?: boolean;
    isDisabled?: boolean;
    isHovered?: boolean;
    isPressed?: boolean;
    isLoading?: boolean;
    isNarrow?: boolean;
    isFakeDisabled?: boolean; // To be able to have a disabled-look-and-feel button but with pointer events available
    form?: string; // To be able to link submit button that lies outside of Form
};

/*
 * Mode is special cases when there is a button
 * with unique style and there is no place for it in design system
 */

const Button: FunctionComponent<ButtonProps> = (props) => {
    const {
        id,
        text,
        a11yName,
        type,
        styling = ButtonStyling.FILLED,
        size = ButtonSize.MEDIUM,
        color = ColorGroupBrand.PRIMARY,
        extraClasses,
        isDisabled,
        isFakeDisabled,
        isHovered,
        isPressed,
        isLoading,
        href,
        hasOpenInBlank,
        onClick,
        qaRole,
        isNarrow = true,
        icon,
        view = ButtonView.DEFAULT,
        form,
    } = props;

    const classname = classNames(extraClasses || '', {
        button: true,
        'is-hovered': isHovered && !isDisabled,
        'is-pressed': (isPressed && !isDisabled) || isLoading,
        'is-loading': isLoading,
        'is-disabled': isDisabled,
        'is-fake-disabled': isFakeDisabled,
        'button--narrow': isNarrow,
        [mapSizeToClassname[size]]: true,
        [mapViewToClassname[view]]: true,
        [mapColorToClassname[color]]: true,
        [mapStylingToClassname[styling]]: true,
    });

    const buttonContent = (
        <Fragment>
            <span className="button__content">
                {icon && <span className="button__icon">{icon}</span>}

                {text && (
                    <span className="button__text">
                        <Action>{text}</Action>
                    </span>
                )}
            </span>

            {isLoading && (
                <span className="button__loader">
                    <Loader />
                </span>
            )}
        </Fragment>
    );

    const baseProps = {
        id,
        onClick,
        className: classname,
    };

    if (href) {
        return (
            <a
                {...baseProps}
                data-qa-role={qaRole || 'button'}
                href={href}
                aria-label={a11yName}
                {...(hasOpenInBlank ? { target: '_blank', rel: 'noopener noreferrer' } : undefined)}
            >
                {buttonContent}
            </a>
        );
    }

    if (type) {
        return (
            <button
                {...baseProps}
                data-qa-role={qaRole || 'button'}
                disabled={isDisabled && !isFakeDisabled}
                type={mapTypeToButtonType[type]}
                form={form}
                aria-label={a11yName}
            >
                {buttonContent}
            </button>
        );
    }

    return (
        <div {...baseProps} data-qa-role={qaRole || 'button'} role={'button'} aria-label={a11yName}>
            {buttonContent}
        </div>
    );
};

export { ButtonSize, ButtonView, ButtonStyling, ButtonType };

export default Button;
